/**
 * Lectura de codigos de barras.
 *
 * Dos caminos, porque una tienda usa los dos:
 *
 * 1. LECTOR USB. Se comporta como un teclado: teclea los digitos muy rapido y
 *    termina con Enter. No hay API de navegador para "leer del lector"; lo que
 *    se hace es escuchar el teclado y distinguir al lector de una persona por
 *    el ritmo. Un humano no teclea doce digitos con 20 ms entre tecla y tecla.
 *
 * 2. CAMARA. Para vender o inventariar desde el celular, sin lector.
 *
 * SOBRE EL DECODIFICADOR DE LA CAMARA, que es la parte que sorprende:
 *
 * Este archivo decia antes "se usa `BarcodeDetector`, la API nativa, sin cargar
 * una libreria de vision por computadora". Eso funciona en Chrome de Android y
 * NO funciona en ningun aparato de Apple: WebKit no implementa la API, asi que
 * en iPhone y en iPad —incluido Chrome de iOS, que por dentro es WebKit—
 * `window.BarcodeDetector` sencillamente no existe. Con la API nativa sola, el
 * boton de escanear no podia mas que esconderse, que es justo lo que se veia.
 *
 * Por eso hay un decodificador de respaldo en WebAssembly (ZXing, vía el
 * ponyfill `barcode-detector`) que:
 *
 *   - Se carga SOLO si el navegador no trae la API nativa. Chrome de Android no
 *     baja un byte de mas.
 *   - Viaja en el propio despliegue, no desde un CDN ajeno. El archivo `.wasm`
 *     se importa con `?url` para que Vite lo publique bajo /assets/ con hash en
 *     el nombre; asi lo cachea el service worker y la caja no depende de que un
 *     tercero siga en linea.
 *   - Expone EXACTAMENTE la misma interfaz que la API nativa, asi que el resto
 *     del archivo no sabe cual de los dos esta usando.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

// El `.wasm` se importa por su direccion, no por su contenido: son 1.04 MB que
// no deben acabar dentro de un modulo de JavaScript. La constante que queda en
// el bundle es una cadena de cuarenta caracteres.
import zxingWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

// ---------------------------------------------------------------------------
// Lector USB (emulacion de teclado)
// ---------------------------------------------------------------------------

/*
 * LA PISTOLA ES UN TECLADO, Y AHI ESTAN LAS DOS TRAMPAS.
 *
 * No hay API de navegador para "leer del lector": el aparato teclea los
 * caracteres muy rapido y termina con Enter. Se le distingue de una persona por
 * el RITMO —nadie teclea doce caracteres con 20 ms entre tecla y tecla— y se le
 * escucha a nivel de VENTANA, para que escanear funcione sin que nadie tenga que
 * hacer clic en un campo primero. Eso ultimo no es comodidad: en el mostrador la
 * persona tiene la pistola en una mano y el producto en la otra, y el foco vive
 * donde lo dejo el ultimo toque.
 *
 * Trampa 1 — LAS TECLAS DEL LECTOR SE CUELAN EN EL CAMPO ENFOCADO. Escuchar en
 * la ventana no impide que el navegador siga entregando cada caracter a donde
 * este el foco. Con el foco en "Nombre" del alta, un disparo dejaba el codigo de
 * barras ESCRITO DENTRO DEL NOMBRE ademas de hacer lo suyo; en la entrada de
 * mercancia caia dentro de un costo o de una cantidad, que es un numero
 * equivocado que nadie vuelve a mirar. Por eso, en cuanto la racha ya no puede
 * ser una persona, las teclas se le quitan al campo (preventDefault) y lo que
 * alcanzo a entrar antes se BORRA al completarse la lectura — y solo si el campo
 * quedo exactamente como lo dejamos, porque si algo mas lo movio no es nuestro.
 *
 * Trampa 2 — DOS PANTALLAS ATENDIENDO EL MISMO DISPARO. Antes cada pantalla
 * apagaba su oyente a mano enumerando las hojas que podia tener abiertas
 * ("!paymentOpen && !cameraOpen && ..."); olvidar una dejaba un escaneo
 * agregando productos DETRAS de una hoja abierta. Ahora hay una sola escucha y
 * un registro por CAPAS: la lectura la atiende la capa de mas arriba y nadie
 * mas. Cada hoja modal ocupa una capa por el solo hecho de estar abierta (ver
 * useScanBlocker en Modal), asi que una hoja que no sabe escanear se traga la
 * lectura en vez de dejarla caer en la pantalla de atras.
 *
 * Lo que este archivo NO hace, a proposito: enfocar campos. Enfocar para "estar
 * listo" abre el teclado del sistema en telefono y en iPad, tapa media pantalla
 * y no le sirve a nadie. La escucha es de ventana justamente para no necesitarlo.
 */

/** Intervalo maximo entre teclas para considerarlo maquina y no persona. */
const MAX_KEY_GAP_MS = 60;

/**
 * Un tropiezo del hilo principal no es una persona.
 *
 * Entre dos teclas del lector puede meterse un repintado, una invalidacion de
 * Realtime o una pausa del recolector de basura. Se tolera UNO por racha: lo que
 * delata a una persona es que TODO su ritmo sea lento, no que haya un hueco.
 */
const MAX_STALL_MS = 250;

/** Longitud minima de un codigo. Debajo de esto es casi seguro tecleo humano. */
const MIN_CODE_LENGTH = 4;

/**
 * A partir de cuantos caracteres se le quitan las teclas al campo enfocado.
 *
 * Es mas exigente que MIN_CODE_LENGTH y la diferencia importa: quitarle teclas a
 * quien esta escribiendo, y ademas borrarle lo escrito, seria peor que la fuga
 * que se quiere evitar. Cuatro digitos rapidos y Enter los produce una persona
 * tecleando "1500" en una cantidad; seis o mas, con todos los huecos por debajo
 * de 60 ms, ya no. Por debajo de este largo la lectura se atiende igual que
 * siempre y no se toca el campo — o sea, ningun codigo que hoy funciona deja de
 * funcionar.
 */
export const CONFIDENT_LENGTH = 6;

/**
 * ¿Este Enter es de una lectura, o hay que dejarlo pasar?
 *
 * UN ENTER QUE NADIE VA A ATENDER NO SE DESTRUYE. Antes la respuesta era solo el
 * largo: con cuatro caracteres o mas, `preventDefault` + `stopPropagation` y a
 * despachar — aunque arriba hubiera una hoja que se iba a tragar el codigo. Eso
 * era invisible mientras Enter no significara nada. En cuanto Enter significa
 * "Cobrar", se convierte en esto:
 *
 *   La cajera teclea `50000` en el bloque numerico y remata con Enter. Cinco
 *   digitos rapidos son una racha de largo 5, el Enter se despacha, la hoja de
 *   cobro lo bloquea... y no pasa nada. Otra vez. Y otra.
 *
 * Asi que se extiende al Enter la asimetria que este archivo ya aplica a las
 * teclas del campo (ver CONFIDENT_LENGTH): el liston para QUITARLE algo a una
 * persona es mas alto que el de atender un codigo.
 *
 *   - Racha de maquina segura (>= CONFIDENT_LENGTH): es una lectura pase lo que
 *     pase. Se la queda, la atienda alguien o no — un codigo de barras que se
 *     cuele como "confirmar" seria muchisimo peor.
 *   - Racha corta (4 o 5, que una persona SI produce) y alguien esperando la
 *     lectura: como siempre. Ningun escaneo que hoy funciona deja de funcionar.
 *   - Racha corta y NADIE esperando: se deja pasar. No se pierde ninguna lectura,
 *     porque esa lectura se iba a tragar de todos modos.
 *
 * Es pura para poder probarla sin navegador, que es como se prueba todo aqui.
 */
export function claimsEnter(bufferLength: number, hasListener: boolean): boolean {
  if (bufferLength < MIN_CODE_LENGTH) return false;
  return bufferLength >= CONFIDENT_LENGTH || hasListener;
}

/** Cota ante un lector mal configurado que nunca manda Enter. */
const MAX_BUFFER = 64;

/** Si la racha se queda a medias, se le devuelven sus teclas a quien escribia. */
const BURST_TIMEOUT_MS = 400;

// --- capas ------------------------------------------------------------------

/**
 * En que capa esta lo que se dibuja. La pantalla es 0; cada hoja modal suma una.
 *
 * Vive aqui y no en components/ui.tsx para que la pieza visual dependa del lector
 * y no al reves: ui.tsx ya importa de todos lados y un ciclo entre los dos
 * compila sin quejarse y revienta en el navegador.
 */
export const ScanLayerContext = createContext(0);

export function useScanLayer(): number {
  return useContext(ScanLayerContext);
}

interface ScanTarget {
  layer: number;
  seq: number;
  /** `null` = capa que BLOQUEA: se traga la lectura y no la atiende nadie. */
  onScan: ((code: string) => void) | null;
}

const targets = new Set<ScanTarget>();
let nextSeq = 0;

/** A quien le toca esta lectura: la capa mas alta, y una sola. */
function topmost(): ScanTarget | null {
  let best: ScanTarget | null = null;

  for (const target of targets) {
    if (best === null) {
      best = target;
      continue;
    }
    if (target.layer !== best.layer) {
      if (target.layer > best.layer) best = target;
      continue;
    }
    // A la misma altura, un oyente de verdad le gana al bloqueo: la hoja bloquea
    // lo que hay DEBAJO de ella, no a su propio contenido. Es lo que deja que el
    // alta y la entrada de mercancia sigan escaneando dentro de su hoja.
    if (best.onScan === null && target.onScan !== null) best = target;
    else if (best.onScan !== null && target.onScan === null) continue;
    else if (target.seq > best.seq) best = target;
  }

  return best;
}

function register(layer: number, onScan: ((code: string) => void) | null): () => void {
  nextSeq += 1;
  const entry: ScanTarget = { layer, seq: nextSeq, onScan };
  targets.add(entry);
  startListening();

  return () => {
    targets.delete(entry);
    stopListening();
  };
}

// --- la racha ---------------------------------------------------------------

type Editable = HTMLInputElement | HTMLTextAreaElement;

interface Burst {
  /**
   * Quien es esta racha.
   *
   * Hace falta porque el LARGO no distingue una racha que muere de otra que
   * nace: si el lector dispara, la racha se aborta por tiempo con largo 8 y el
   * lector vuelve a disparar, el largo va 8 -> 1, que es exactamente lo que se
   * ve al empezar cualquier racha. Quien mira desde fuera (`machineRun`, el
   * teclado en pantalla) tendria que DEDUCIR la costura, y esa clase de
   * deduccion es la que falla en el borde. Con un identificador no hay nada que
   * deducir: es otra racha porque tiene otro numero.
   */
  id: number;
  /** Todo lo tecleado en la racha. */
  buffer: string;
  /** Lo que alcanzo a entrar al campo antes de reconocer a la maquina. */
  leaked: string;
  /** Lo que se le quito al campo despues de reconocerla. */
  swallowed: string;
  /** Ya se gasto el unico tropiezo tolerado. */
  stalled: boolean;
  /** El campo que tenia el foco al empezar, con su estado de entonces. */
  field: Editable | null;
  before: string;
  selStart: number;
  selEnd: number;
}

let burst: Burst | null = null;
let nextBurstId = 0;
let lastKeyAt = 0;
let abortTimer = 0;
let listening = false;

/**
 * La racha viva, para quien tenga que apartarse de ella.
 *
 * La usa el teclado en pantalla (`useKeypad`, components/NumericPad.tsx): los
 * caracteres de una lectura SI le llegan —este archivo solo les hace
 * `preventDefault`, que cancela la insercion en el campo pero no detiene la
 * propagacion—, asi que sin esto un disparo del lector con la hoja de cantidad
 * abierta escribiria trece digitos dentro de un peso.
 *
 * Se expone el DATO y no la decision, a proposito: quien pregunta ya sabe que
 * hacer con su propio estado. Lo que no puede es tener su propia idea de que es
 * una maquina y que es una persona — ese es el trabajo de este archivo, y dos
 * criterios para la misma pregunta acaban discrepando (§13).
 */
export function machineRun(): { id: number; length: number } | null {
  return burst === null ? null : { id: burst.id, length: burst.buffer.length };
}

function editableTarget(target: EventTarget | null): Editable | null {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ? target : null;
}

/** El caret, o el final del texto en los campos que no tienen (numero, correo). */
function caretOf(field: Editable): [number, number] {
  try {
    const { selectionStart, selectionEnd } = field;
    if (typeof selectionStart === 'number' && typeof selectionEnd === 'number') {
      return [selectionStart, selectionEnd];
    }
  } catch {
    /* Un input de tipo numero no expone caret. */
  }
  const end = field.value.length;
  return [end, end];
}

/**
 * Escribe en un campo CONTROLADO por React.
 *
 * Poner field.value a secas no sirve: React guarda el ultimo valor que rindio y
 * no ve el cambio, asi que el siguiente repintado lo deshace. Hay que usar el
 * asignador nativo del prototipo y disparar "input", que es lo que React escucha.
 */
function writeField(field: Editable, value: string, selStart: number, selEnd: number): void {
  const proto =
    field instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;

  if (setter) setter.call(field, value);
  else field.value = value;

  field.dispatchEvent(new Event('input', { bubbles: true }));

  try {
    field.setSelectionRange(selStart, selEnd);
  } catch {
    /* Los campos sin caret lanzan aqui; el valor ya quedo bien. */
  }
}

/** Le quita al campo lo que se colo antes de reconocer al lector. */
function undoLeak(current: Burst): void {
  const { field } = current;
  if (field === null || current.leaked === '' || current.swallowed === '') return;

  const expected =
    current.before.slice(0, current.selStart) +
    current.leaked +
    current.before.slice(current.selEnd);

  // Si el campo no quedo EXACTAMENTE como lo dejamos —un onChange que
  // transforma, un maxLength, alguien escribiendo a la vez— no es nuestro y no
  // se toca. Peor que dejar un residuo es borrar lo que alguien acaba de poner.
  if (field.value !== expected) return;

  writeField(field, current.before, current.selStart, current.selEnd);
}

/** Le devuelve al campo las teclas que se le quitaron a una racha que no era. */
function replaySwallowed(current: Burst): void {
  const { field } = current;
  if (field === null || current.swallowed === '') return;

  const [start, end] = caretOf(field);
  const value = field.value;
  const caret = start + current.swallowed.length;
  writeField(field, value.slice(0, start) + current.swallowed + value.slice(end), caret, caret);
}

function clearBurst(): void {
  window.clearTimeout(abortTimer);
  abortTimer = 0;
  burst = null;
}

/** La racha no era una lectura: se deshace lo que se le hizo al campo. */
function abortBurst(): void {
  const current = burst;
  clearBurst();
  if (current !== null) replaySwallowed(current);
}

function armAbortTimer(): void {
  window.clearTimeout(abortTimer);
  abortTimer = window.setTimeout(abortBurst, BURST_TIMEOUT_MS);
}

function startBurst(field: Editable | null): Burst {
  const [selStart, selEnd] = field === null ? [0, 0] : caretOf(field);
  nextBurstId += 1;
  const next: Burst = {
    id: nextBurstId,
    buffer: '',
    leaked: '',
    swallowed: '',
    stalled: false,
    field,
    before: field === null ? '' : field.value,
    selStart,
    selEnd,
  };
  burst = next;
  return next;
}

// --- la escucha, una sola para toda la aplicacion ---------------------------

function onKeyDown(event: KeyboardEvent): void {
  const target = event.target as HTMLElement | null;

  // El campo dedicado se encarga solo: el lector escribe ahi y la pantalla
  // resuelve su Enter. Atenderlo aqui tambien lo procesaria dos veces.
  if (target?.closest('[data-scan-input]')) {
    abortBurst();
    return;
  }

  // Donde se escribe de verdad —una nota, un motivo de merma— no se interpreta
  // nada como codigo.
  if (target instanceof HTMLTextAreaElement || target?.isContentEditable === true) {
    abortBurst();
    return;
  }

  // Un atajo del teclado o del navegador no es una lectura. Tampoco lo es lo que
  // sale de un metodo de entrada (acentos, teclado chino).
  if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) {
    abortBurst();
    return;
  }

  // Una tecla DETENIDA repite igual de rapido que teclea el lector, y "55555"
  // seguido de Enter es un codigo perfectamente creible. El lector nunca repite.
  if (event.repeat) {
    abortBurst();
    return;
  }

  const now = performance.now();
  const gap = now - lastKeyAt;
  lastKeyAt = now;

  if (event.key === 'Enter') {
    const current = burst;

    if (current !== null && claimsEnter(current.buffer.length, topmost()?.onScan != null)) {
      const code = current.buffer;
      undoLeak(current);
      clearBurst();

      // El Enter del lector no envia el formulario donde este parado el foco, ni
      // llega a la capa de teclas (`lib/keys.ts`, que escucha en BURBUJA de
      // ventana): `stopPropagation` desde captura corta la cadena entera. Es lo
      // que hace seguro que Enter signifique "Cobrar" sin que un disparo cobre.
      event.preventDefault();
      event.stopPropagation();

      // Si arriba hay una hoja que no sabe escanear, la lectura se queda ahi. Es
      // deliberado: un codigo de barras metido en un peso o en un costo es un
      // numero equivocado que nadie vuelve a mirar, y un disparo que no hace
      // nada se ve en el acto — quien tiene la pistola en la mano vuelve a
      // disparar.
      topmost()?.onScan?.(code);
      return;
    }

    abortBurst();
    return;
  }

  // Shift, F5, las flechas: no son caracteres. lastKeyAt ya se actualizo, asi que
  // el Shift que precede a una mayuscula no rompe el ritmo de la racha.
  if (event.key.length !== 1) return;

  const field = editableTarget(target);
  let current = burst;

  // La racha solo sigue viva si el hueco la avala. Como cada tecla pasa por
  // aqui, un buffer largo YA significa "ritmo de maquina" y no hace falta
  // contar los huecos rapidos aparte.
  const continues =
    current !== null &&
    current.field === field &&
    (gap <= MAX_KEY_GAP_MS ||
      (!current.stalled && current.buffer.length >= 2 && gap <= MAX_STALL_MS));

  if (current === null || !continues) {
    abortBurst();
    current = startBurst(field);
  } else if (gap > MAX_KEY_GAP_MS) {
    current.stalled = true;
  }

  if (current.buffer.length >= CONFIDENT_LENGTH - 1) {
    // Ya no puede ser una persona: de aqui en adelante las teclas no entran al
    // campo, y lo que se colo antes se borrara al completarse la lectura.
    event.preventDefault();
    current.swallowed += event.key;
  } else {
    current.leaked += event.key;
  }

  current.buffer += event.key;

  if (current.buffer.length > MAX_BUFFER) abortBurst();
  else armAbortTimer();
}

function startListening(): void {
  if (listening) return;
  listening = true;
  window.addEventListener('keydown', onKeyDown, true);
}

function stopListening(): void {
  if (!listening || targets.size > 0) return;
  listening = false;
  window.removeEventListener('keydown', onKeyDown, true);
  abortBurst();
}

// --- lo que usan las pantallas ----------------------------------------------

/**
 * Atiende el lector USB mientras el componente este montado.
 *
 * Se registra en la capa donde vive quien llama, asi que una hoja abierta le gana
 * a la pantalla de atras sin que ninguna de las dos tenga que enterarse de la
 * otra. `enabled` sigue existiendo para lo que la capa no puede saber —por
 * ejemplo, que mande la camara mientras su visor esta abierto—.
 */
export function useUsbScanner(onScan: (code: string) => void, enabled = true): void {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const layer = useScanLayer();

  useEffect(() => {
    if (!enabled) return;
    return register(layer, (code) => onScanRef.current(code));
  }, [enabled, layer]);
}

/**
 * Ocupa una capa sin atender nada: lo que se lea encima de esto no baja.
 *
 * Lo usa `Modal`, y por eso ninguna hoja tiene que acordarse de apagar el lector
 * de la pantalla que la abrio. Se registra una capa MAS ARRIBA de donde se
 * escribio, que es donde vive su contenido: asi bloquea lo de abajo y deja pasar
 * lo suyo.
 */
export function useScanBlocker(active: boolean): void {
  const layer = useScanLayer() + 1;

  useEffect(() => {
    if (!active) return;
    return register(layer, null);
  }, [active, layer]);
}

// ---------------------------------------------------------------------------
// Camara: el decodificador
// ---------------------------------------------------------------------------

/** Formatos que de verdad aparecen en un abarrotes mexicano. */
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'] as const;

type Format = (typeof FORMATS)[number];

/**
 * Lo unico que este archivo necesita de un decodificador. La API nativa y el
 * respaldo en WebAssembly cumplen las dos con esta forma, que es el motivo de
 * haber elegido un ponyfill fiel a la especificacion y no una libreria con
 * interfaz propia: el dia que WebKit implemente la API, se borra el respaldo y
 * no se toca una linea mas.
 */
interface Detector {
  detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]>;
}

interface NativeDetectorConstructor {
  new (options?: { formats?: string[] }): Detector;
  getSupportedFormats?: () => Promise<string[]>;
}

/**
 * El decodificador se construye UNA vez por pestaña y se reutiliza.
 *
 * Importa por dos razones: instanciar el modulo de WebAssembly cuesta cerca de
 * un segundo, y hacerlo en cada apertura de la camara desperdiciaria ese
 * segundo cada vez que el cajero escanea un producto.
 */
let detectorPromise: Promise<Detector> | null = null;

function nativeConstructor(): NativeDetectorConstructor | null {
  const ctor = (globalThis as { BarcodeDetector?: NativeDetectorConstructor }).BarcodeDetector;
  return typeof ctor === 'function' ? ctor : null;
}

async function createDetector(): Promise<Detector> {
  const Native = nativeConstructor();

  if (Native) {
    try {
      // No basta con que la clase exista: hay que preguntarle que sabe leer. Un
      // navegador que la expone pero no soporta EAN-13 no sirve para una tienda,
      // y pasarle un formato que no conoce hace que el constructor lance.
      const supported = (await Native.getSupportedFormats?.()) ?? [];
      const usable = FORMATS.filter((format): format is Format => supported.includes(format));
      if (usable.length > 0) return new Native({ formats: usable });
    } catch {
      // Existe pero no responde como debe. Se usa el respaldo.
    }
  }

  // Respaldo: ZXing compilado a WebAssembly. La importacion es dinamica para
  // que Vite lo mande a su propio trozo y Chrome de Android nunca lo descargue.
  const { BarcodeDetector, prepareZXingModule } = await import('barcode-detector/ponyfill');

  prepareZXingModule({
    overrides: {
      // Sin esto el modulo busca el `.wasm` en jsDelivr. Una caja que deja de
      // escanear porque un CDN ajeno tuvo un mal dia no es una caja.
      locateFile: (path: string, prefix: string) =>
        path.endsWith('.wasm') ? zxingWasmUrl : `${prefix}${path}`,
    },
  });

  return new BarcodeDetector({ formats: [...FORMATS] });
}

function loadDetector(): Promise<Detector> {
  detectorPromise ??= createDetector().catch((err: unknown) => {
    // Un fallo de carga no debe quedar memorizado para siempre: si fue una
    // caida momentanea de la red, el siguiente intento debe poder reintentarlo.
    detectorPromise = null;
    throw err;
  });
  return detectorPromise;
}

// ---------------------------------------------------------------------------
// Camara: acuse de lectura
// ---------------------------------------------------------------------------

/**
 * Un lector que no avisa cuando leyo se siente descompuesto: el cajero se queda
 * mirando la pantalla sin saber si apuntar otra vez.
 *
 * Se usan las dos señales porque ninguna cubre a todos los aparatos: Safari de
 * iOS no implementa `navigator.vibrate` —o sea que en iPhone NO hay vibracion
 * posible desde la web— y en una tienda con radio prendido el tono corto puede
 * perderse. Juntas, siempre queda al menos una.
 */
let audioContext: AudioContext | null = null;

function acknowledge(): void {
  navigator.vibrate?.(40);

  try {
    // Se crea al primer acuse, no al cargar el modulo: un AudioContext creado
    // sin que el usuario haya tocado nada nace suspendido y ademas iOS lo
    // cuenta como uso de audio.
    audioContext ??= new AudioContext();
    if (audioContext.state === 'suspended') void audioContext.resume();

    const now = audioContext.currentTime;
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();

    // Agudo y muy corto, como el de un lector de mostrador. Con rampas en la
    // ganancia porque un tono que arranca y corta en seco suena a chasquido.
    oscillator.frequency.value = 1760;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.11);

    oscillator.connect(gain).connect(audioContext.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.12);
  } catch {
    // Sin audio se escanea igual.
  }
}

// ---------------------------------------------------------------------------
// Camara: el ciclo de lectura
// ---------------------------------------------------------------------------

export type CameraStatus =
  | 'idle'
  | 'loading'
  | 'starting'
  | 'scanning'
  | 'insecure-context'
  | 'denied'
  | 'no-camera'
  | 'error';

export const CAMERA_STATUS_MESSAGES: Record<CameraStatus, string> = {
  idle: '',
  loading: 'Preparando el lector...',
  starting: 'Encendiendo la camara...',
  scanning: 'Acerca el codigo de barras al recuadro',
  'insecure-context':
    'La camara solo funciona con una direccion segura (https) o en esta misma computadora. Si entraste por la direccion IP de la red, abre el sistema desde su enlace publicado.',
  denied:
    'No diste permiso para usar la camara. Habilitalo en los ajustes del navegador para este sitio y vuelve a intentar.',
  'no-camera': 'Este aparato no tiene una camara que se pueda usar.',
  error: 'No se pudo abrir la camara. Cierra otras aplicaciones que la esten usando e intenta de nuevo.',
};

/** Resolucion maxima que se manda a decodificar, por el lado mas largo. */
const MAX_SCAN_EDGE = 1280;

/**
 * Tiempo minimo entre intentos de lectura.
 *
 * Antes el ciclo iba pegado a `requestAnimationFrame`, o sea sesenta intentos
 * por segundo. Con la API nativa se notaba poco; con el decodificador de
 * WebAssembly eso es tener el telefono al cien por ciento de un nucleo, y un
 * iPhone caliente baja la frecuencia de la camara justo cuando mas se necesita.
 * Diez por segundo es mas rapido que el pulso de la mano y no calienta nada.
 */
const SCAN_INTERVAL_MS = 100;

/**
 * Espera antes de volver a aceptar el MISMO codigo.
 *
 * No es una optimizacion: la camara ve el mismo codigo en cada cuadro, asi que
 * sin esto un refresco se agregaria diez veces por segundo. El valor deja
 * escanear dos productos iguales seguidos —levantar y volver a apuntar toma
 * mas de un segundo— sin que uno solo se cuente doble.
 */
const DEBOUNCE_MS = 1200;

interface TorchControl {
  /** El aparato tiene lampara y el navegador deja encenderla. */
  available: boolean;
  on: boolean;
  toggle: () => void;
}

export interface CameraScanner {
  status: CameraStatus;
  videoRef: RefObject<HTMLVideoElement | null>;
  torch: TorchControl;
}

/*
 * La lampara del telefono NO esta en los tipos del DOM: es una extension que
 * implementa Chrome de Android y que la especificacion de Media Capture todavia
 * trae aparte. Se declara aqui ampliando las interfaces del navegador —que son
 * abiertas justamente para esto— en lugar de resolverlo con un molde a `any` en
 * cada uso. Asi el compilador sigue revisando el resto de la llamada, y el dia
 * que `torch` entre a los tipos de fabrica este bloque se borra sin tocar nada
 * mas.
 */
declare global {
  interface MediaTrackCapabilities {
    torch?: boolean;
  }
  interface MediaTrackConstraintSet {
    torch?: ConstrainBoolean;
  }
}

export function useCameraScanner(onScan: (code: string) => void, active: boolean): CameraScanner {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<CameraStatus>('idle');
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const trackRef = useRef<MediaStreamTrack | null>(null);

  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!active) {
      setStatus('idle');
      return;
    }

    // `getUserMedia` no existe fuera de un contexto seguro. Es el caso tipico de
    // entrar desde el celular por http://192.168.x.x durante el desarrollo, y
    // conviene explicarlo en lugar de mostrar un error del navegador.
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setStatus('insecure-context');
      return;
    }

    let cancelled = false;
    let stream: MediaStream | null = null;
    let timer = 0;

    const canvas = document.createElement('canvas');
    // `willReadFrequently` le dice al navegador que este lienzo se lee muchas
    // veces por segundo; sin el, algunos motores lo suben a la GPU y cada
    // lectura de pixeles obliga a traerlo de vuelta.
    const context = canvas.getContext('2d', { willReadFrequently: true });

    const stop = (): void => {
      cancelled = true;
      window.clearTimeout(timer);
      trackRef.current = null;
      stream?.getTracks().forEach((track) => track.stop());
      const video = videoRef.current;
      if (video) video.srcObject = null;
    };

    /**
     * Un intento de lectura. Devuelve el codigo o null.
     *
     * Se decodifica sobre un lienzo propio y no sobre el elemento de video para
     * poder acotar la resolucion: la camara entrega 1280x720 o mas, y cada
     * cuadro que cruza a WebAssembly se copia entero.
     */
    const readFrame = async (detector: Detector): Promise<string | null> => {
      const video = videoRef.current;
      if (!video || !context) return null;
      // HAVE_CURRENT_DATA: antes de esto no hay un cuadro que dibujar.
      if (video.readyState < 2) return null;

      const { videoWidth: width, videoHeight: height } = video;
      if (!width || !height) return null;

      const scale = Math.min(1, MAX_SCAN_EDGE / Math.max(width, height));
      const targetWidth = Math.round(width * scale);
      const targetHeight = Math.round(height * scale);
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
      }

      context.drawImage(video, 0, 0, targetWidth, targetHeight);

      try {
        const results = await detector.detect(canvas);
        return results[0]?.rawValue?.trim() || null;
      } catch {
        // Un cuadro que no se pudo analizar —movido, a contraluz— no es un
        // fallo: se sigue con el siguiente.
        return null;
      }
    };

    const run = async (): Promise<void> => {
      // El decodificador primero: si hay que bajar el WebAssembly, mas vale
      // hacerlo mientras el usuario todavia esta acomodando el telefono, y no
      // dejar la camara prendida esperando.
      setStatus('loading');
      let detector: Detector;
      try {
        detector = await loadDetector();
      } catch {
        if (!cancelled) setStatus('error');
        return;
      }
      if (cancelled) return;

      setStatus('starting');
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            // La camara trasera es la que apunta al producto.
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      } catch (err) {
        if (cancelled) return;
        const name = (err as { name?: string }).name;
        setStatus(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'denied'
            : name === 'NotFoundError' || name === 'OverconstrainedError'
              ? 'no-camera'
              : 'error',
        );
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        // `playsInline` es obligatorio en iOS: sin el, Safari abre el video a
        // pantalla completa y tapa la caja.
        video.playsInline = true;
        video.muted = true;
        /*
         * NO se espera a `play()`. Su promesa resuelve cuando la reproduccion
         * EMPEZO, o sea cuando llego el primer cuadro, y si la camara se
         * concede pero no entrega cuadros —otra aplicacion la tiene tomada, un
         * aparato en mal estado— esa promesa no resuelve nunca: la pantalla se
         * quedaba clavada en "Encendiendo la camara...", sin ciclo de lectura y
         * sin manera de salir mas que cerrando. Medido en el navegador, con un
         * flujo sin cuadros: quince segundos ahi parado.
         *
         * Lanzarla sin esperar no pierde nada, porque `readFrame` ya comprueba
         * `readyState` en cada intento: el ciclo simplemente no lee hasta que
         * hay algo que leer, y arranca solo en cuanto lo hay.
         */
        void video.play().catch(() => {
          // Safari rechaza con AbortError si el elemento se re-dibuja en medio.
          // El video ya tiene su fuente; el ciclo espera cuadros por su cuenta.
        });
      }
      if (cancelled) return;

      const [track] = stream.getVideoTracks();
      if (track) {
        trackRef.current = track;
        // `getCapabilities` no existe en Safari, de ahi la llamada opcional.
        setTorchAvailable(Boolean(track.getCapabilities?.().torch));
      }

      setStatus('scanning');

      let lastCode = '';
      let lastAt = 0;

      const tick = async (): Promise<void> => {
        if (cancelled) return;
        const startedAt = performance.now();

        const code = await readFrame(detector);
        if (cancelled) return;

        if (code) {
          const now = performance.now();
          if (code !== lastCode || now - lastAt > DEBOUNCE_MS) {
            lastCode = code;
            lastAt = now;
            acknowledge();
            onScanRef.current(code);
          }
        }

        // Se descuenta lo que tardo el intento: con la API nativa el ciclo
        // corre a los diez por segundo previstos, y con WebAssembly —que tarda
        // mas que el intervalo— corre tan rapido como puede sin encimarse.
        const elapsed = performance.now() - startedAt;
        timer = window.setTimeout(() => void tick(), Math.max(0, SCAN_INTERVAL_MS - elapsed));
      };

      void tick();
    };

    void run();
    return stop;
  }, [active]);

  // La lampara se apaga sola al cerrar la camara: el `track.stop()` de la
  // limpieza la apaga fisicamente, pero el estado tiene que acompañarlo o al
  // reabrir el boton apareceria encendido con la lampara apagada.
  useEffect(() => {
    if (!active) {
      setTorchAvailable(false);
      setTorchOn(false);
    }
  }, [active]);

  const toggleTorch = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setTorchOn((previous) => {
      const next = !previous;
      // Si el aparato la rechaza, el estado vuelve a apagado: un boton
      // encendido con la lampara apagada es peor que no tener boton.
      void track.applyConstraints({ advanced: [{ torch: next }] }).catch(() => setTorchOn(false));
      return next;
    });
  }, []);

  return {
    status,
    videoRef,
    torch: { available: torchAvailable, on: torchOn, toggle: toggleTorch },
  };
}

/**
 * True si tiene sentido ofrecer el escaneo con camara en este aparato.
 *
 * La pregunta que responde es "¿esto se agarra con la mano?", y se contesta con
 * una consulta de medios sobre el hardware y NO con la cadena de agente, porque
 * un iPad con "Solicitar sitio para escritorio" se anuncia como una Mac: por
 * agente es indistinguible de una computadora, por hardware sigue siendo una
 * pantalla tactil.
 *
 * La consulta es `pointer: coarse` —el medio de apuntado PRINCIPAL es un dedo—
 * y no `any-pointer: coarse`, que seria "existe alguno grueso". La diferencia
 * es una maquina concreta: la all-in-one tactil del mostrador tiene dedo Y
 * raton, asi que responde `any-pointer: coarse` y se ganaba un boton que ahi no
 * sirve —su camara apunta a la cara del cajero y a un lado tiene el lector USB—.
 * Con `pointer` gana el principal: en la all-in-one es el raton y el boton
 * desaparece; en telefono, tablet e iPad —incluido el iPad con teclado y
 * trackpad, donde iOS sigue reportando el dedo como principal— se queda.
 *
 * Lo que NO se comprueba aqui es si la camara va a arrancar. Contexto inseguro,
 * permiso denegado o falta de camara se explican DENTRO de la hoja, con su
 * motivo: esconder el boton por esos casos deja al usuario sin saber por que
 * desaparecio una funcion que le dijeron que existe.
 */
const HANDHELD_QUERY = '(pointer: coarse)';

export function useCameraScanAvailable(): boolean {
  const [available, setAvailable] = useState(() => window.matchMedia(HANDHELD_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(HANDHELD_QUERY);
    const update = (): void => setAvailable(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return available;
}
